<template>
  <div class="saved-collections-list q-pa-md">
    <div class="saved-collections-list__wrapper">
      <div v-if="rows" class="saved-collections-list__wrapper--content">
        <mi-table
          ref="miTable"
          custom-header
          :columns="columns"
          :rows="rows"
          :loading="loading || isPinLoading"
          class="saved-collections full-height"
          column-sort-order="da"
          loading-label="Loading results. Please wait ..."
          row-key="id"
          hide-pagination
          sticky-header
          striped
          wrap-cells
          :on-change-sort-table-direction="onChangeSortTableDirection"
          :on-change-sort-table-column-visibility="onChangeSortTableColumnVisibility"
        >
          <template #top-left>
            <div class="row">
              <div class="saved-collections-list__header">
                <div>
                  <div class="row items-center">
                    <p class="q-table__title saved-collections-list__header-title q-mb-sm">Saved Collections</p>
                    <div class="saved-collections-list__header-actions">
                      <mi-refresh-btn
                        :is-loading="loading || isPinLoading"
                        class="q-ml-sm q-mr-sm refresh-btn"
                        icon-size="14px"
                        @clicked-refresh-btn="fetchSavedCollections({ isPinned: isPinnedFilterActive })"
                      ></mi-refresh-btn>
                      <mi-separator vertical></mi-separator>
                      <mi-btn
                        v-if="!isPinnedFilterActive"
                        class="q-ml-sm q-mr-sm pin-btn"
                        icon="pin-outline"
                        flat
                        round
                        @click="pinFilterClick()"
                      ></mi-btn>
                      <mi-btn
                        v-else
                        class="pin-btn"
                        icon="pin"
                        flat
                        round
                        @click="unpinFilterClick()"
                      ></mi-btn>
                      <mi-separator vertical></mi-separator>
                      <saved-collections-search-field
                        :fetch-saved-collections="fetchSavedCollections"
                        :is-pinned-filter-active="isPinnedFilterActive"
                      ></saved-collections-search-field>
                    </div>
                  </div>
                  <span class="saved-collections-list__header-counter q-mb-sm">
                    {{ totalElements }} saved
                  </span>
                </div>
              </div>
            </div>
          </template>
          <template v-if="!loading" #no-data>
            <div class="column">
              <img src="@/assets/images/vm/saved-collections-empty.svg" alt="no-saved-collections" />
              <div class="no-data-message">
                <p class="q-mb-sm">Collection saved can be found here.</p>
                <p class="no-data-message__text">
                  To store a collection, click Save as collection in search page.
                </p>
              </div>
            </div>
          </template>
          <template #body-cell-actions="{ row }">
            <mi-td>
              <div class="actions-column">
                <mi-btn color="transparent" @click.stop>
                  <q-icon class="text-dark" size="small">
                    <img src="@/assets/images/dots_vertical.svg" alt="Saved tables actions" />
                  </q-icon>
                  <mi-menu>
                    <q-list class="actions-column__list">
                      <q-item v-close-popup clickable @click="togglePin(row)">
                        <q-item-section>
                          <q-item-label v-if="!row.isPinned">Pin this collection</q-item-label>
                          <q-item-label v-else>Unpin this collection</q-item-label>
                        </q-item-section>
                      </q-item>
                      <q-item v-close-popup clickable @click="handleToggleOpenModalEditCollectionName(row)">
                        <q-item-section>
                          <q-item-label>Edit name</q-item-label>
                        </q-item-section>
                      </q-item>
                      <q-item v-close-popup clickable @click="onDropdownDeleteClick(row)">
                        <q-item-section>
                          <q-item-label>Delete</q-item-label>
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </mi-menu>
                </mi-btn>
              </div>
            </mi-td>
          </template>
          <template #body-cell-send="{ row }">
            <mi-td>
              <mi-btn
                class="send-to-search-btn"
                color="primary"
                size="sm"
                @click.stop="$emit('send-to-search', row)"
              >
                Send to Search
              </mi-btn>
            </mi-td>
          </template>
          <template #body-cell-name="{ row }">
            <div class="saved-collections-name-cell">
              <span class="saved-collections-name-cell__label">
                {{ row.name }}
              </span>

              <mi-tr-action-btn
                v-if="!row.isPinned"
                class="saved-collections-name-cell__pin saved-collections-name-cell__pin--hover"
                icon="pin-outline"
                flat
                @click.stop="togglePin(row)"
              ></mi-tr-action-btn>

              <mi-tr-action-btn
                v-else
                class="saved-collections-name-cell__pin"
                :persistent="true"
                icon="pin"
                flat
                @click.stop="togglePin(row)"
              ></mi-tr-action-btn>
            </div>
          </template>
        </mi-table>
      </div>
    </div>
  </div>
  <saved-collection-dialog
    :handle-submit="editCollectionName"
    :handle-close-modal="handleToggleOpenModalEditCollectionName"
    :save-collection-modal="editCollectionNameModalValue"
    is-edit
    :save-collections-loading="isUpdateCollectionLoading"
    :collection-name-to-be-edit="collectionToBeEdit?.name || ''"
  ></saved-collection-dialog>
  <delete-modal
    :selected-row="selectedRow"
    :delete-modal="deleteModalRef"
    :delete-collections="deleteCollections"
    :close-delete-modal="closeDeleteModal"
  ></delete-modal>
</template>

<script>
  import { deleteCollection, updateCollection } from '@/api/rest/saved-collections'
  import notify from '@/utils/notify'
  import * as notificationTypes from '@/plugins/quasar/notifications/notificationTypes'
  import { ref } from 'vue'
  import MiTrActionBtn from '@/packages/@mi-library/MiTable/MiTrActionBtn.vue'
  import MiRefreshBtn from '../../packages/@mi-components/MiRefreshBtn/MiRefreshBtn.vue'
  import MiSeparator from '../../packages/@mi-library/MiSeparator/MiSeparator.vue'
  import SavedCollectionsSearchField from './SavedCollectionsSearchField.vue'
  import DeleteModal from './dialogs/DeleteCollection.vue'
  import SavedCollectionDialog from '../search/search-summary/save-collection-dialog/SaveCollectionDialog.vue'

  export default {
    name: 'SavedCollectionsList',
    components: {
      MiTrActionBtn,
      SavedCollectionsSearchField,
      MiSeparator,
      MiRefreshBtn,
      DeleteModal,
      SavedCollectionDialog
    },
    props: {
      rows: {
        type: Array,
        required: true
      },
      totalElements: {
        type: Number,
        required: true
      },
      columns: {
        type: Array,
        required: true
      },
      loading: {
        type: Boolean,
        required: true
      },
      fetchSavedCollections: {
        type: Function,
        required: true
      },
      onChangeSortTableDirection: {
        type: Function,
        default: () => {},
        required: true
      },
      onChangeSortTableColumnVisibility: {
        type: Function,
        default: () => {},
        required: true
      },
      unpinFilterClick: {
        type: Function,
        default: () => {},
        required: true
      },
      pinFilterClick: {
        type: Function,
        default: () => {},
        required: true
      },
      isPinnedFilterActive: {
        type: Boolean,
        required: true
      }
    },
    emits: ['send-to-search'],
    setup(props) {
      const editCollectionNameModalValue = ref(false)
      const collectionToBeEdit = ref({})
      const isUpdateCollectionLoading = ref(false)
      const deleteModalRef = ref(false)
      const selectedRow = ref([])
      const isPinLoading = ref(false)

      const onDropdownDeleteClick = row => {
        deleteModalRef.value = true
        selectedRow.value = row
      }

      const togglePin = async row => {
        try {
          isPinLoading.value = true
          await updateCollection({
            collectionId: row.id,
            name: row.name,
            isPinned: !row.isPinned
          })

          setTimeout(() => {
            props.fetchSavedCollections({ isPinned: props.isPinnedFilterActive })
            isPinLoading.value = false
          }, 500)
        }
        catch (error) {
          notify({
            title: 'Error',
            content: 'Pin or unpin action failed.',
            type: 'negative'
          })
          isPinLoading.value = false
        }
      }

      const handleToggleOpenModalEditCollectionName = (row = {}) => {
        editCollectionNameModalValue.value = !editCollectionNameModalValue.value
        collectionToBeEdit.value = row
      }

      const editCollectionName = async newCollectionName => {
        try {
          isUpdateCollectionLoading.value = true
          await updateCollection({
            collectionId: collectionToBeEdit.value.id,
            name: newCollectionName,
            isPinned: collectionToBeEdit.value.isPinned
          })
        }
        catch (e) {
          console.log(e)
        }
        finally {
          isUpdateCollectionLoading.value = false
          handleToggleOpenModalEditCollectionName()
          await props.fetchSavedCollections({ isPinned: props.isPinnedFilterActive })
        }
      }

      const deleteCollections = rowId => {
        deleteModalRef.value = false
        deleteCollection(rowId).then(() => {
          props.fetchSavedCollections({ isPinned: props.isPinnedFilterActive })
          notify({
            content: 'Collection was successfully deleted!',
            type: notificationTypes.BASIC_INFO,
            position: 'bottom',
            progress: false,
            closeIcon: false,
            timeout: 2000
          })
        })
      }

      const closeDeleteModal = () => {
        deleteModalRef.value = false
      }

      return {
        handleToggleOpenModalEditCollectionName,
        editCollectionNameModalValue,
        editCollectionName,
        collectionToBeEdit,
        isUpdateCollectionLoading,
        onDropdownDeleteClick,
        deleteModalRef,
        selectedRow,
        deleteCollections,
        closeDeleteModal,
        togglePin,
        isPinLoading
      }
    }
  }
</script>

<style lang="scss" scoped>
  .saved-collections-list {
    &__header-counter {
      color: #5b6f85;
      font-size: 12px;
      font-style: normal;
      font-family: $mi-typography-font-family-condensed;
      font-weight: 700;
      line-height: 130%;
    }

    &__header-actions {
      display: flex;
      flex-direction: row;
      align-content: center;

      .q-separator {
        height: 44px;
      }
    }

    &__header-title {
      font-size: 24px;
      font-family: $mi-typography-font-family-condensed;
      font-weight: 700;
      line-height: 130%;
      color: $mi-anthracite-800;
    }

    &__wrapper {
      .q-inner-loading {
        height: 60vh;
      }

      &--content {
        width: 100%;
        height: calc(100vh - 300px);

        .items-center {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          justify-content: flex-start;
        }

        &__actions-list {
          width: 160px;
        }
      }
    }
  }

  .mi-table-wrapper .q-table__bottom--nodata {
    align-items: center;
    top: 300px;

    .no-data-message {
      font-size: 14px;
      font-family: $mi-typography-font-family-condensed;
      font-weight: 700;
      color: $mi-anthracite-800;
      display: flex;
      flex-direction: column;
      align-items: center;

      &__text {
        font-weight: 400;
      }
    }
  }

  .actions-column {
    display: flex;
    justify-items: center;

    &__list {
      width: 150px;
    }
  }

  .send-to-search-btn {
    // stylelint-disable declaration-no-important
    width: 130px;
    min-width: 100px;
    min-height: 32px;
    font-size: 14px !important;
  }

  .saved-collections-name-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 7px;

    &__label {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__pin--hover {
      opacity: 0;
    }
  }

  ::v-deep(.mi-icon-pin) {
    font-size: 12px !important;
  }

  ::v-deep(.mi-icon-pin-outline) {
    font-size: 12px !important;
  }

  ::v-deep(.q-btn.pin-btn) {
    margin: 4px !important;
  }

  ::v-deep(.q-btn.pin-btn .q-icon) {
    font-size: 16px !important;
  }

  ::v-deep(.q-tr:hover) {
    .saved-collections-name-cell__pin--hover {
      opacity: 1;
    }
  }
</style>
